import { useState } from 'react';

function americanToDecimal(american) {
	if (american > 0) return american / 100 + 1;
	return 100 / Math.abs(american) + 1;
}

function decimalToAmerican(decimal) {
	if (decimal >= 2) return Math.round((decimal - 1) * 100);
	return Math.round(-100 / (decimal - 1));
}

function decimalToFractional(decimal) {
	const profit = decimal - 1;
	if (profit <= 0) return '';
	const tolerance = 1e-6;
	let num = 1, den = 1;
	let bestNum = 1, bestDen = 1;
	let bestError = Math.abs(profit - 1);
	for (den = 1; den <= 100; den++) {
		num = Math.round(profit * den);
		if (num < 1) continue;
		const error = Math.abs(profit - num / den);
		if (error < bestError) {
			bestError = error;
			bestNum = num;
			bestDen = den;
		}
		if (error < tolerance) break;
	}
	return `${bestNum}/${bestDen}`;
}

function fractionalToDecimal(fractional) {
	const parts = fractional.split('/');
	if (parts.length !== 2) return NaN;
	const num = parseFloat(parts[0]);
	const den = parseFloat(parts[1]);
	if (!den || isNaN(num) || isNaN(den)) return NaN;
	return num / den + 1;
}

export const OddsConverter = () => {
	const [american, setAmerican] = useState('');
	const [decimal, setDecimal] = useState('');
	const [fractional, setFractional] = useState('');
	const [implied, setImplied] = useState('');
	const [lastEdited, setLastEdited] = useState(null);

	const formatDec = (dec) => dec.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
	const formatAm = (am) => (am > 0 ? `+${am}` : `${am}`);
	const formatProb = (dec) => ((1 / dec) * 100).toFixed(2);

	const handleAmericanChange = (val) => {
		setAmerican(val);
		setLastEdited('american');
		const num = parseFloat(val);
		if (isNaN(num) || num === 0 || (num > -100 && num < 100)) {
			setDecimal('');
			setFractional('');
			setImplied('');
			return;
		}
		const dec = americanToDecimal(num);
		setDecimal(formatDec(dec));
		setFractional(decimalToFractional(dec));
		setImplied(formatProb(dec));
	};

	const handleDecimalChange = (val) => {
		setDecimal(val);
		setLastEdited('decimal');
		const num = parseFloat(val);
		if (isNaN(num) || num <= 1) {
			setAmerican('');
			setFractional('');
			setImplied('');
			return;
		}
		setAmerican(formatAm(decimalToAmerican(num)));
		setFractional(decimalToFractional(num));
		setImplied(formatProb(num));
	};

	const handleFractionalChange = (val) => {
		setFractional(val);
		setLastEdited('fractional');
		const dec = fractionalToDecimal(val);
		if (isNaN(dec) || dec <= 1) {
			setAmerican('');
			setDecimal('');
			setImplied('');
			return;
		}
		setDecimal(formatDec(dec));
		setAmerican(formatAm(decimalToAmerican(dec)));
		setImplied(formatProb(dec));
	};

	const handleImpliedChange = (val) => {
		setImplied(val);
		setLastEdited('implied');
		const prob = parseFloat(val);
		if (isNaN(prob) || prob <= 0 || prob >= 100) {
			setAmerican('');
			setDecimal('');
			setFractional('');
			return;
		}
		const dec = 100 / prob;
		setDecimal(formatDec(dec));
		setAmerican(formatAm(decimalToAmerican(dec)));
		setFractional(decimalToFractional(dec));
	};

	const handleReset = () => {
		setAmerican('');
		setDecimal('');
		setFractional('');
		setImplied('');
		setLastEdited(null);
	};

	const hasResult = lastEdited !== null && (american || decimal || fractional || implied);

	return (
		<div>
			<div className="calc-section">
				<h3 className="calc-section-title">Enter odds in any format</h3>
				<div className="calc-grid calc-grid-4">
					<div className="calc-input-group">
						<label className="calc-label">American</label>
						<input
							className="calc-input"
							type="text"
							placeholder="+150 or -110"
							value={american}
							onChange={(e) => handleAmericanChange(e.target.value)}
						/>
					</div>
					<div className="calc-input-group">
						<label className="calc-label">Decimal</label>
						<input
							className="calc-input"
							type="text"
							placeholder="2.50"
							value={decimal}
							onChange={(e) => handleDecimalChange(e.target.value)}
						/>
					</div>
					<div className="calc-input-group">
						<label className="calc-label">Fractional</label>
						<input
							className="calc-input"
							type="text"
							placeholder="3/2"
							value={fractional}
							onChange={(e) => handleFractionalChange(e.target.value)}
						/>
					</div>
					<div className="calc-input-group">
						<label className="calc-label">Implied Probability (%)</label>
						<input
							className="calc-input"
							type="text"
							placeholder="40.00"
							value={implied}
							onChange={(e) => handleImpliedChange(e.target.value)}
						/>
					</div>
				</div>
			</div>

			{hasResult && (
				<div className="calc-results">
					<h4 className="calc-results-title">Conversion Results</h4>
					<div className="calc-result-row">
						<span className="calc-result-label">American Odds</span>
						<span className="calc-result-value">{american}</span>
					</div>
					<div className="calc-result-row">
						<span className="calc-result-label">Decimal Odds</span>
						<span className="calc-result-value">{decimal}</span>
					</div>
					<div className="calc-result-row">
						<span className="calc-result-label">Fractional Odds</span>
						<span className="calc-result-value">{fractional}</span>
					</div>
					<div className="calc-result-row">
						<span className="calc-result-label">Implied Probability</span>
						<span className="calc-result-value highlight">{implied ? `${implied}%` : ''}</span>
					</div>
				</div>
			)}

			<button className="calc-reset-btn" onClick={handleReset}>Reset</button>
		</div>
	);
};
